/** In-app handoff when a caller cannot navigate to `/mobile-viewer` directly. */
const STORAGE_KEY = 'tpl-open-ufdr-viewer';

export type UfdrViewerRequest = {
  acquisitionId?: number;
  url?: string;
  path?: string;
  caseFolderName?: string;
  label?: string;
  fileStatusId?: string;
  artifactType?: string;
  view?: string;
  rowIds?: string[];
  threadId?: string;
  family?: string;
  search?: string;
  filters?: string;
  record?: string;
  page?: string;
};

export function readUfdrViewerRequest(params: { get(name: string): string | null }): UfdrViewerRequest | null {
  const rawId = params.get('acquisitionId');
  const acquisitionId = rawId == null || rawId === '' ? undefined : Number(rawId);
  const url = params.get('url') ?? undefined;
  if (acquisitionId == null && !url) return null;
  if (acquisitionId != null && !Number.isFinite(acquisitionId)) return null;
  const row = params.get('row');
  return {
    ...(acquisitionId != null ? { acquisitionId } : {}),
    ...(url ? { url } : {}),
    ...(params.get('path') ? { path: params.get('path') ?? undefined } : {}),
    ...(params.get('caseFolderName')
      ? { caseFolderName: params.get('caseFolderName') ?? undefined }
      : {}),
    label: params.get('label') ?? undefined,
    fileStatusId: params.get('fileStatusId') ?? undefined,
    artifactType: params.get('type') ?? undefined,
    view: params.get('view') ?? undefined,
    rowIds: row ? row.split(',').filter(Boolean) : undefined,
    threadId: params.get('threadId') ?? undefined,
    ...(params.get('family') ? { family: params.get('family') ?? undefined } : {}),
    ...(params.get('q') ? { search: params.get('q') ?? undefined } : {}),
    ...(params.get('f') ? { filters: params.get('f') ?? undefined } : {}),
    ...(params.get('record') ? { record: params.get('record') ?? undefined } : {}),
    ...(params.get('page') ? { page: params.get('page') ?? undefined } : {}),
  };
}

/** Writes the browse keys the viewer keeps in the address bar. */
export function writeUfdrBrowseParams(
  current: URLSearchParams,
  patch: Partial<Pick<UfdrViewerRequest, 'family' | 'artifactType' | 'view' | 'search' | 'filters' | 'record' | 'threadId' | 'page'>>,
): URLSearchParams {
  const next = new URLSearchParams(current.toString());
  const keys: Array<[keyof typeof patch, string]> = [
    ['family', 'family'],
    ['artifactType', 'type'],
    ['view', 'view'],
    ['search', 'q'],
    ['filters', 'f'],
    ['record', 'record'],
    ['threadId', 'threadId'],
    ['page', 'page'],
  ];
  for (const [field, param] of keys) {
    if (!(field in patch)) continue;
    const value = patch[field];
    if (!value) next.delete(param);
    else next.set(param, value);
  }
  return next;
}

/** Inverse of {@link readUfdrViewerRequest}, for the flag-off page handoff. */
export function ufdrViewerRequestSearchParams(request: UfdrViewerRequest): URLSearchParams {
  const params = new URLSearchParams();
  if (request.acquisitionId != null) params.set('acquisitionId', String(request.acquisitionId));
  if (request.url) params.set('url', request.url);
  if (request.path) params.set('path', request.path);
  if (request.caseFolderName) params.set('caseFolderName', request.caseFolderName);
  if (request.label) params.set('label', request.label);
  if (request.fileStatusId) params.set('fileStatusId', request.fileStatusId);
  if (request.artifactType) params.set('type', request.artifactType);
  if (request.view) params.set('view', request.view);
  if (request.rowIds?.length) params.set('row', request.rowIds.join(','));
  if (request.threadId) params.set('threadId', request.threadId);
  if (request.family) params.set('family', request.family);
  if (request.search) params.set('q', request.search);
  if (request.filters) params.set('f', request.filters);
  if (request.record) params.set('record', request.record);
  if (request.page) params.set('page', request.page);
  return params;
}

/** Overlay context for a stashed request. Keeps the browse keys the page URL uses. */
export function ufdrPreviewFromRequest(request: UfdrViewerRequest) {
  return {
    url: request.url ?? '',
    label:
      request.label ||
      (request.acquisitionId != null ? `Acquisition ${request.acquisitionId}` : undefined),
    fileInfo: null,
    fileStatusId: request.fileStatusId ?? null,
    path: request.path,
    caseFolderName: request.caseFolderName,
    acquisitionId: request.acquisitionId,
    artifactType: request.artifactType,
    view: request.view,
    rowIds: request.rowIds,
    threadId: request.threadId,
    family: request.family,
    search: request.search,
    filters: request.filters,
    record: request.record,
    page: request.page,
  };
}

/**
 * Flag on opens the theme overlay. Flag off sends the same query to
 * `/mobile-viewer`, where Farnham's viewer is mounted.
 */
export function consumeUfdrViewerHandoff(args: {
  themeEnabled: boolean;
  openOverlay: (request: UfdrViewerRequest) => void;
  navigate: (href: string) => void;
}): void {
  const request = takeUfdrViewerRequest();
  if (!request) return;
  if (!args.themeEnabled) {
    const query = ufdrViewerRequestSearchParams(request).toString();
    args.navigate(query ? `/mobile-viewer?${query}` : '/mobile-viewer');
    return;
  }
  args.openOverlay(request);
}

export function stashUfdrViewerRequest(params: URLSearchParams): void {
  if (typeof sessionStorage === 'undefined') return;
  if (!readUfdrViewerRequest(params)) return;
  sessionStorage.setItem(STORAGE_KEY, params.toString());
}

export function takeUfdrViewerRequest(): UfdrViewerRequest | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(STORAGE_KEY);
  return readUfdrViewerRequest(new URLSearchParams(raw));
}
